import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import useViewTracker from "../../hooks/useViewTracker";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, Users, Calendar, Filter, Star, PlaneTakeoff } from "lucide-react";
import { useData } from "../../context/DataContext";
import { formatCurrency } from "../../lib/format";
import SEO from "../../components/SEO";

const PreviewCabs = () => {
    const { cabs } = useData();
    const { t } = useTranslation();
    const [search] = useSearchParams();
    const isTransfer = search.get("focus") === "transfer";
    useViewTracker("page", isTransfer ? "/airport-transfers" : "/cab-booking");
    const [seat, setSeat] = useState("any");
    const [sort, setSort] = useState("price-asc");

    /* Preserve quick-quote params (from/to/date/passengers) when navigating to the booking page */
    const preservedQS = useMemo(() => {
        const carry = ["from", "to", "date", "passengers"];
        const out = new URLSearchParams();
        carry.forEach((k) => {
            const v = search.get(k);
            if (v) out.set(k, v);
        });
        return out.toString();
    }, [search]);

    const visible = useMemo(() => {
        let list = (cabs || []).filter((c) => c.active);
        if (seat === "small") list = list.filter((c) => c.seats <= 5);
        if (seat === "medium") list = list.filter((c) => c.seats >= 6 && c.seats <= 12);
        if (seat === "large") list = list.filter((c) => c.seats > 12);
        list.sort((a, b) =>
            sort === "price-asc"
                ? a.ratePerDay - b.ratePerDay
                : sort === "price-desc"
                ? b.ratePerDay - a.ratePerDay
                : a.seats - b.seats,
        );
        return list;
    }, [cabs, seat, sort]);

    const serviceJsonLd = {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Kerala Cab Booking",
        provider: {
            "@type": "TravelAgency",
            name: "SouthTrip.in by LuxSaCabs",
            url: "https://southtrip.in",
        },
        areaServed: { "@type": "Place", name: "Kerala, India" },
        serviceType: "Private cab booking with driver",
    };

    return (
        <div data-testid="preview-cabs">
            <SEO
                title={t("seo.cabsTitle")}
                description={t("seo.cabsDescription")}
                path="/cab-booking"
                jsonLd={serviceJsonLd}
                keywords="kerala cab booking, kerala taxi service, private driver kerala, munnar cab booking, alleppey taxi, cochin cab service"
            />
            {/* HEADER */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 pb-8 sm:pb-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-end">
                    <div className="md:col-span-8">
                        <div className="hz-pill">
                            {isTransfer ? (
                                <>
                                    <PlaneTakeoff className="w-3 h-3 mr-1" /> Airport / city transfer · one-way
                                </>
                            ) : (
                                <>Fleet · 12 vehicles</>
                            )}
                        </div>
                        <h1 className="font-hz-display text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] mt-4 sm:mt-5">
                            {t("seo.cabsH1")}
                        </h1>
                    </div>
                    <div className="md:col-span-4">
                        <p className="text-[var(--ink-soft)] text-sm sm:text-base leading-relaxed">
                            {isTransfer
                                ? "Quick one-way rides from Cochin / Trivandrum / Calicut airports to your hotel — verified drivers, fixed price, no surge. Pay driver on arrival."
                                : (<>All rates include a verified driver, AC, fuel, tolls and{" "}<b>100 km/day</b>. No prepayment — pay the driver on arrival.</>)}
                        </p>
                    </div>
                </div>
            </section>

            {/* FILTERS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 sticky top-[68px] sm:top-[72px] z-20 bg-[var(--bg)] border-y border-[var(--border)]">
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap text-sm">
                    <div className="flex items-center gap-1.5 text-[var(--ink-soft)] text-xs sm:text-sm">
                        <Filter className="w-3.5 h-3.5" /> Filters
                    </div>
                    {[
                        { v: "any", l: "All" },
                        { v: "small", l: "≤5 seats" },
                        { v: "medium", l: "6–12" },
                        { v: "large", l: "13+" },
                    ].map((o) => (
                        <button
                            key={o.v}
                            onClick={() => setSeat(o.v)}
                            className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition ${
                                seat === o.v
                                    ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                                    : "border-[var(--border)] text-[var(--ink-soft)] hover:border-[var(--ink)]"
                            }`}
                        >
                            {o.l}
                        </button>
                    ))}
                    <div className="ml-auto flex items-center gap-2">
                        <select
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            className="px-4 py-1.5 rounded-full border border-[var(--border)] text-xs font-semibold bg-white appearance-none pr-8"
                            style={{
                                backgroundImage:
                                    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1.5l5 5 5-5' stroke='%230F172A' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right 12px center",
                            }}
                        >
                            <option value="price-asc">Price · low to high</option>
                            <option value="price-desc">Price · high to low</option>
                            <option value="seats-asc">Seats</option>
                        </select>
                    </div>
                </div>
            </section>

            {/* CAB LIST */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                    {visible.map((c, i) => (
                        <article
                            key={c.id}
                            className="hz-card p-3 sm:p-4 md:p-5 grid grid-cols-12 gap-4 sm:gap-5 items-center"
                            data-testid={`preview-cab-${c.id}`}
                        >
                            <div className="col-span-12 sm:col-span-5 relative">
                                <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-[var(--bg-surface)]">
                                    <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
                                </div>
                                <div className="absolute top-3 left-3 hz-pill bg-white/90">
                                    {c.category}
                                </div>
                            </div>
                            <div className="col-span-12 sm:col-span-7 flex flex-col gap-2 sm:gap-3">
                                <div className="flex items-center gap-1 text-xs text-[var(--ink-soft)]">
                                    <Star className="w-3 h-3 fill-[var(--ink)] text-[var(--ink)]" /> 4.7 · 320+ trips
                                </div>
                                <div>
                                    <h2 className="font-hz-display text-xl sm:text-2xl md:text-3xl leading-tight">{c.name}</h2>
                                    <p className="text-xs sm:text-sm text-[var(--ink-soft)] mt-1 sm:mt-1.5 line-clamp-2">{c.description}</p>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-[var(--ink-soft)]">
                                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {c.seats} seats</span>
                                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {c.kmLimit} km/day</span>
                                </div>
                                <div className="flex items-end justify-between pt-2 sm:pt-3 border-t border-[var(--border)]">
                                    <div>
                                        <div className="text-[10px] text-[var(--ink-softer)] uppercase tracking-widest">Per day</div>
                                        <div className="font-hz-display text-2xl sm:text-3xl leading-none mt-1">
                                            {formatCurrency(c.ratePerDay)}
                                        </div>
                                    </div>
                                    <Link
                                        to={(() => {
                                            const qs = new URLSearchParams(preservedQS);
                                            if (isTransfer) qs.set("tripType", "One Way");
                                            const qsStr = qs.toString();
                                            return `/cab-booking/book/${c.id}${qsStr ? `?${qsStr}` : ""}`;
                                        })()}
                                        className="hz-btn-primary text-xs sm:text-sm py-2.5 px-4 sm:px-6"
                                    >
                                        <span className="hidden sm:inline">{isTransfer ? "Book transfer" : "Book now"}</span>
                                        <span className="sm:hidden">Book</span>
                                        <ArrowUpRight className="w-3.5 h-3.5" />
                                    </Link>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            {/* ===== SEO LONG-FORM CONTENT ===== */}
            <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 lg:py-24 prose-style" data-testid="cabs-seo-content">
                <h2 className="font-hz-display text-3xl md:text-4xl tracking-tight">Reliable Kerala cab booking for every kind of trip</h2>
                <p className="text-[var(--ink-soft)] mt-4 leading-relaxed">
                    SouthTrip.in by LuxSaCabs is a verified Kerala cab booking and private driver service operating across all 14 districts of Kerala. Whether you need a sedan for an airport transfer, an Innova SUV for a family backwaters trip, or a 17-seat tempo traveller for a multi-city Kerala tour, every booking comes with a Kerala-trained chauffeur, fuel and 100 km/day already included in the rate. There is no surge pricing, no advance payment and no hidden charges — you pay the driver directly on arrival in cash, UPI or card.
                </p>
                <h3 className="font-hz-display text-2xl mt-10">Cab booking from every Kerala city</h3>
                <p className="text-[var(--ink-soft)] mt-3 leading-relaxed">
                    Pick up your private cab from <Link to="/airport-transfers" className="hz-link">Cochin International Airport (COK)</Link>, Trivandrum (TRV), Calicut (CCJ), or any railway station — Ernakulam Junction, Aluva, Trivandrum Central, Kollam Junction, Kozhikode. Our most popular city-to-city Kerala cab routes are Cochin → Munnar, Cochin → Alleppey, Trivandrum → Kovalam, Calicut → Wayanad and the full backwaters loop covering Alleppey, Kumarakom and Marari beach.
                </p>
                <h3 className="font-hz-display text-2xl mt-10">Sightseeing, long-distance and custom Kerala trips</h3>
                <p className="text-[var(--ink-soft)] mt-3 leading-relaxed">
                    Beyond simple point-to-point cab booking, SouthTrip drivers are trained sightseeing guides familiar with every tea estate in Munnar, every spice plantation in Thekkady and every quiet houseboat jetty in Alleppey. For long-distance Kerala taxi service we operate inter-state runs to Bangalore, Mysore, Coimbatore, Madurai, Goa and Pondicherry. Need a driver for a wedding circuit, a corporate roadshow or a 10-day South India self-drive companion? Our <Link to="/tour-packages" className="hz-link">Kerala tour packages</Link> page lists 25+ ready-made itineraries; for anything else, contact us for a free quote.
                </p>
                <h3 className="font-hz-display text-2xl mt-10">What makes a SouthTrip cab booking different</h3>
                <ul className="text-[var(--ink-soft)] mt-3 space-y-2 list-disc pl-6 leading-relaxed">
                    <li>Verified Kerala-trained drivers fluent in English and Malayalam — Hindi and Arabic on request.</li>
                    <li>Transparent per-day rates including driver, fuel, AC and 100 km/day.</li>
                    <li>Pay direct to driver on arrival — no online prepayment, no booking fees.</li>
                    <li>24/7 phone & WhatsApp support before, during and after every trip.</li>
                    <li>Choice of sedan, SUV (Innova Crysta), tempo traveller, Force Urbania and 26-seat mini-bus.</li>
                </ul>
                <h3 className="font-hz-display text-2xl mt-10">Need to plan something bigger?</h3>
                <p className="text-[var(--ink-soft)] mt-3 leading-relaxed">
                    Combine your Kerala cab booking with a private <Link to="/airport-transfers" className="hz-link">airport transfer</Link>, a multi-day <Link to="/tour-packages" className="hz-link">tour package</Link> or browse popular <Link to="/destinations" className="hz-link">Kerala destinations</Link>. For a personalised quote our team is reachable on WhatsApp at +91 80-75814740 or via the <Link to="/contact" className="hz-link">contact page</Link>.
                </p>
            </section>
        </div>
    );
};

export default PreviewCabs;
